/**
 * The sign-up dialog: [data-signup] buttons open it ("Sign Up Click"), the
 * plan select swaps the billing terms, and its form posts to /sign-up, which
 * answers with the FastSpring checkout in a new tab ("Go Billing").
 *
 * The plans of /pricing ([data-checkout-form]) post to /sign-up directly and
 * leave for the checkout in the same tab: both events at once.
 *
 * The gentle nudge: after several successful uploads within a day, a free
 * visitor who selects files sees the dialog first, with a button to go on
 * with the free version.
 */

import { trackEvent } from "./analytics";
import { closeDialog, dialogById, openDialog } from "./lib/dialog";
import { find, findAll } from "./lib/dom";
import { readItem, writeItem } from "./lib/storage";

const NUDGE_KEY = "dz.successful-uploads";
export const NUDGE_THRESHOLD = 5;
export const NUDGE_WINDOW_MS = 24 * 60 * 60 * 1000;

interface UploadCounter {
  readonly count: number;
  readonly since: number; // epoch ms of the first upload of the window
}

let continueFree: (() => void) | null = null;
let nudgedThisPage = false;

function readCounter(now: number): UploadCounter {
  const raw = readItem(NUDGE_KEY);
  if (raw === null) return { count: 0, since: now };
  try {
    const data: unknown = JSON.parse(raw);
    if (
      typeof data === "object" &&
      data !== null &&
      "count" in data &&
      "since" in data &&
      typeof data.count === "number" &&
      typeof data.since === "number" &&
      now - data.since < NUDGE_WINDOW_MS
    ) {
      return { count: data.count, since: data.since };
    }
  } catch {
    // a damaged value starts a new window
  }
  return { count: 0, since: now };
}

/** Count a successful upload (localStorage only; no cookies). */
export function recordSuccessfulUpload(now: number = Date.now()): void {
  const counter = readCounter(now);
  writeItem(
    NUDGE_KEY,
    JSON.stringify({ count: counter.count + 1, since: counter.since }),
  );
}

/** Whether a free visitor should see the sign-up dialog before the next files. */
export function shouldNudge(now: number = Date.now()): boolean {
  return !nudgedThisPage && readCounter(now).count >= NUDGE_THRESHOLD;
}

/**
 * Open the sign-up dialog. With `onContinue` it is the nudge: the dialog
 * offers "continue with the free version", which closes it and calls back.
 */
export function openSignup(onContinue?: () => void): void {
  const dialog = dialogById("signup-dialog");
  if (dialog === null) return;
  continueFree = onContinue ?? null;
  if (onContinue) nudgedThisPage = true;
  const block = find(dialog, "[data-signup-continue]", HTMLElement);
  if (block) block.hidden = onContinue === undefined;
  trackEvent("sign_up_click");
  openDialog(dialog);
}

function showOffer(dialog: HTMLElement, offer: string): void {
  for (const element of findAll(dialog, "[data-offer]", HTMLElement)) {
    element.hidden = element.dataset["offer"] !== offer;
  }
}

export function initSignup(): void {
  document.addEventListener("click", (event) => {
    const target = event.target;
    if (target instanceof Element && target.closest("[data-signup]")) {
      event.preventDefault();
      openSignup();
    }
  });
  document.addEventListener("submit", (event) => {
    const target = event.target;
    if (target instanceof Element && target.matches("[data-checkout-form]")) {
      trackEvent("sign_up_click");
      trackEvent("go_billing");
    }
  });
  const dialog = dialogById("signup-dialog");
  if (dialog === null) return;
  const plan = find(dialog, "select[name=offer]", HTMLSelectElement);
  plan?.addEventListener("change", () => {
    showOffer(dialog, plan.value);
  });
  find(dialog, "[data-signup-form]", HTMLFormElement)?.addEventListener(
    "submit",
    () => {
      trackEvent("go_billing");
      // the checkout opens in a new tab; this page stays as it was
      continueFree = null;
      window.setTimeout(() => {
        closeDialog(dialog);
      }, 0);
    },
  );
  find(dialog, "[data-continue-free]", HTMLButtonElement)?.addEventListener(
    "click",
    () => {
      const next = continueFree;
      continueFree = null;
      closeDialog(dialog);
      next?.();
    },
  );
  dialog.addEventListener("close", () => {
    continueFree = null;
  });
}
